import { assert } from './assert.js';

/**
 * Constructor args
 */
export interface GamoraUtilsDebugArgs {
  namespace: string;
  color?: number;
}
/**
 * logging args
 */
export interface GamoraUtilsDebugLogArgs {
  trace?: boolean;
  traceMessage?: string;
  error?: unknown;
  output?: boolean;
}

/**
 * Global Configuration
 */
interface GamoraUtilsDebugGlobalConfiguration {
  logging: Map<string, RegExp>;
  warningEnabled: boolean;
  supportedColors: number[];
  enableISOTime: boolean;
  enableLoggingOnCreate: boolean;
}

/**
 * Configuration
 */
interface GamoraUtilsDebugConfiguration {
  namespace: string;
  color: number;
}

/**
 * Configuration
 */
interface GamoraUtilsDebugTime {
  current: number;
  previous: number;
  difference: number;
}

/**
 * GamoraUtilsDebug
 * @public
 */
export class GamoraUtilsDebug {
  private _config: GamoraUtilsDebugConfiguration;
  private _T: GamoraUtilsDebugTime;

  /**
   * Constructor
   * @param opts
   */
  constructor(opts: GamoraUtilsDebugArgs) {
    this._config = {
      namespace: opts.namespace,
      color:
        opts.color ??
        GamoraUtilsDebug.Configuration.supportedColors[
          Math.floor(
            Math.random() *
              GamoraUtilsDebug.Configuration.supportedColors.length,
          )
        ],
    };

    this._T = {
      current: Date.now(),
      previous: Date.now(),
      difference: 0,
    };
  }

  /**
   * Gets local Configuration
   * @returns UtilDebugConfiguration
   */
  get config() {
    return this._config;
  }

  /**
   * Configuration
   * @post all namespaces are logged by default
   * @type UtilsDebugConfiguration
   */
  static Configuration: GamoraUtilsDebugGlobalConfiguration = {
    logging: new Map([]),
    warningEnabled: true,
    supportedColors: [6, 2, 3, 4, 5, 1],
    enableISOTime: true,
    enableLoggingOnCreate: true,
  };

  /**
   * Set timing
   * @private
   */
  _setT() {
    const currentTime = Date.now();
    this._T.previous = this._T.current;
    this._T.difference = currentTime - this._T.previous;
    this._T.current = currentTime;
  }

  /**
   * Check if we can log using regexp
   */
  _canLog() {
    // Enabled
    for (const regularExp of GamoraUtilsDebug.Configuration.logging.values()) {
      if (regularExp.test(this.config.namespace)) {
        return true;
      }
    }

    // Default disabled
    return false;
  }

  /**
   * Format debug output
   * @param input The input string
   * @returns Output
   */
  format(input: string): string {
    const color = this.config.color;
    const namespace = this.config.namespace;
    const date = new Date().toISOString() + ' ';
    const colorCode = '\u001B[3' + (color < 8 ? color : '8;5;' + color);

    // Format
    const isoTimePrefix = `${date}`;
    const namespacePrefix = ` ${colorCode};1m${namespace} \u001B[0m`;
    const timeSuffix = ` ${colorCode};1m+${this._T.difference}ms \u001B[0m`;

    // Output
    if (GamoraUtilsDebug.Configuration.enableISOTime) {
      return isoTimePrefix + namespacePrefix + input + timeSuffix;
    } else {
      return namespacePrefix + input + timeSuffix;
    }
  }

  /**
   * Logs a debug message
   * @param message The log message
   * @param optionalParams Additional parameters
   */
  log(message: string, args: GamoraUtilsDebugLogArgs = {}): void {
    if (this._canLog() || args.output) {
      this._setT();
      console.log(this.format(message));

      // Include stack trace
      if (args.trace) {
        if (args.traceMessage) {
          console.log(args.traceMessage);
        } else {
          console.trace();
        }
      }

      // Prints error and additional stack trace if error object doesn't
      // contain a stack trace.
      if (args.error) {
        console.error(args.error);
        if (!(args.error as { stack?: string })?.stack) {
          console.trace();
        }
      }
    }
  }

  /**
   * Warn a debug message
   * @param message The log message
   * @param optionalParams Additional parameters
   */
  warn(message: string, args: GamoraUtilsDebugLogArgs = {}): void {
    if (GamoraUtilsDebug.Configuration.warningEnabled) {
      this.log(message, { ...args, output: true });
    }
  }

  /**
   * Logs a trace debug message. For errors, use error() instead.
   * @param message The log message
   */
  trace(message: string, traceMessage?: string): void {
    this.log(message, { trace: true, traceMessage });
  }

  /**
   * Logs an error message
   * @param message The log message
   * @param error The error object
   */
  error(message: string, error: unknown): void {
    this.log(message, { error });
  }
}

/**
 * Sets global Configuration values
 * @param key The Configuration key
 * @param value The Configuration value
 */
export function debugSetConfiguration<
  K extends keyof GamoraUtilsDebugGlobalConfiguration,
>(key: K, value: GamoraUtilsDebugGlobalConfiguration[K]) {
  GamoraUtilsDebug.Configuration[key] = value;
}

/**
 * Creates a new debugging instance
 * @param args Constructor arguments
 * @returns GamoraUtilsDebug
 */
export function debugCreate(args: GamoraUtilsDebugArgs): GamoraUtilsDebug {
  if (GamoraUtilsDebug.Configuration.enableLoggingOnCreate) {
    debugEnableLogging(args.namespace);
  }

  return new GamoraUtilsDebug(args);
}

/**
 * Enables debugging log namespaces
 * @param regularExps Namespaces regexp
 */
export function debugEnableLoggingByRegExp(...regularExps: RegExp[]): void {
  for (const regularExp of regularExps) {
    GamoraUtilsDebug.Configuration.logging.set(
      regularExp.toString(),
      regularExp,
    );
  }
}

/**
 * Enables debugging log namespaces
 * @param regularExps Namespaces regexp
 */
export function debugEnableLogging(...namespaces: string[]): void {
  for (const namespace of namespaces) {
    // Create regular exp
    const regularExp = new RegExp(`/${namespace}/`);

    // Enable
    GamoraUtilsDebug.Configuration.logging.set(
      regularExp.toString(),
      regularExp,
    );
  }
}

/**
 * Enable all log namespaces
 * @param namespaces Namespaces
 */
export function debugEnableAllLogging(): void {
  debugSetConfiguration('logging', new Map([['/(.*)/', /(.*)/]]));
}

/**
 * Disables debugging log namespaces
 * @param namespaces Namespaces regexp
 */
export function debugDisableLogging(...namespaces: string[]): void {
  for (const namespace of namespaces) {
    // Create regular exp
    const regularExp = new RegExp(`/${namespace}/`);

    // Disable
    assert(
      GamoraUtilsDebug.Configuration.logging.has(regularExp.toString()),
      namespace + ' does not exist in logging.',
    );
    GamoraUtilsDebug.Configuration.logging.delete(regularExp.toString());
  }
}

/**
 * Disables debugging log namespaces
 * @param namespaces Namespaces regexp
 */
export function debugDisableLoggingByRegexp(...regularExps: RegExp[]): void {
  for (const regularExp of regularExps) {
    assert(
      GamoraUtilsDebug.Configuration.logging.has(regularExp.toString()),
      'Regular expression does not exist in logging.',
    );
    GamoraUtilsDebug.Configuration.logging.delete(regularExp.toString());
  }
}

/**
 * Disables all debugging log namespaces
 * @param namespaces Namespaces
 */
export function debugDisableAllLogging(): void {
  debugSetConfiguration('logging', new Map([]));
}

/**
 * Disables all warning messages
 */
export function debugEnableWarning(): void {
  debugSetConfiguration('warningEnabled', true);
}

/**
 * Disables all warning messages
 */
export function debugDisableWarning(): void {
  debugSetConfiguration('warningEnabled', false);
}
